import type { DiscoverPlayerDataResponse, DiscoverPlayerResponse } from "libs/apiClient";
import { ContentCell } from "features/discover/ContentCell";
import client from "libs/client";
import { useId } from "react";
import { FootChoices, GenderOptions } from "features/discover/types";
import { DiscoverPlayerDetail } from "features/discover/DiscoverPlayerDetail";
import { useToast } from "ui-kit";

export function DiscoverPlayerDataDetail({
	playerData
}: {
	playerData: DiscoverPlayerDataResponse
}) {
	const { addSuccessToast, addErrorToast } = useToast();

	const updateEtlMutation = client.postDiscoverPlayerDataUpdateEtl.useMutation({
		onSuccess: (response) => {
			const count = response?.data?.gamesQueued;
			if (count !== undefined) {
				addSuccessToast(`Data search ETL queued for ${count} distinct game(s).`);
			}
		},
		onError: () => {
			addErrorToast("Could not queue ETL. Try again or contact support.");
		},
	});

	const contentProps = {
		id: playerData.id,
		mutation: client.patchDiscoverPlayerData.useMutation(),
		invalidationKey: 'discoverPlayerDataDetail',
	};

	const { data: countries, isLoading: isCountriesLoading } = client.getDiscoverCountries.useQuery({});

	const playerDataProps = [
		{
			key: useId(),
			title: "First Name",
			field: "firstName",
			type: "string",
			value: playerData.firstName,
		},
		{
			key: useId(),
			title: "Last Name",
			field: "lastName",
			type: "string",
			value: playerData.lastName,
		},
		{
			key: useId(),
			title: "Height",
			field: "height",
			type: "number",
			value: playerData.height,
		},
		{
			key: useId(),
			title: "Weight",
			field: "weight",
			type: "number",
			value: playerData.weight,
		},
		{
			key: useId(),
			title: "Birthday",
			field: "birthday",
			type: "date",
			value: playerData.birthday,
		},
		{
			key: useId(),
			title: "Gender",
			field: "gender",
			type: "list",
			value: playerData.gender,
			options: GenderOptions,
		},
		{
			key: useId(),
			title: "Strongest foot",
			field: "strongestFoot",
			type: "list",
			value: playerData.strongestFoot,
			options: FootChoices,
		},
		{
			key: useId(),
			title: "Played in national team",
			field: "playedInNationalTeam",
			type: "boolean",
			value: playerData.playedInNationalTeam,
		},
		{
			key: useId(),
			title: "Nationality",
			field: "nationality",
			type: "list",
			value: playerData.nationality,
			isSelectLoading: isCountriesLoading,
			options: countries?.data || [],
		},
		{
			key: useId(),
			title: "Nationality 2",
			field: "nationality2",
			type: "list",
			value: playerData.nationality2,
			isSelectLoading: isCountriesLoading,
			options: countries?.data || [],
		},
		{
			key: useId(),
			title: "Tran link",
			field: "tranLink",
			type: "string",
			value: playerData.tranLink,
		},
		{
			key: useId(),
			title: "Federation link",
			field: "federationLink",
			type: "string",
			value: playerData.federationLink,
		},
	];

    return (
		<>
			<div className="discover-card__pre-header">
				<div className="discover-card__title" style={{ marginTop: 0 }}>
					Player data
				</div>
				<button
					type="button"
					className="admin-button admin-button--primary"
					disabled={updateEtlMutation.isLoading}
					onClick={() => updateEtlMutation.mutate({ id: playerData.id })}
					style={{ marginBottom: '10px' }}
				>
					Update ETL
				</button>
			</div>
			<div className="discover-card">
				<div className="cell-grid">
					{playerDataProps.map((props, index) => {
						return <ContentCell {...contentProps} {...props} key={props.key} />
					})}
				</div>
			</div>

			{playerData.players.map((player: DiscoverPlayerResponse, index) => (
				<div style={{marginTop: "80px"}} key={`player-${player.id}`}>
					<DiscoverPlayerDetail
						player={player}
						playerData={playerData}
						key={`player-detail-${index}`}
					/>
				</div>
			))}
		</>
	)
}
